// Reviews (T-901, D20, SCREENS S17): organizer ↔ accepted participants of a completed event,
// within 14 days after it ends. One review per author and target per event; resending edits it.
import { z } from 'zod';

export const REVIEW_COMMENT_MAX = 300;
export const REVIEW_WINDOW_DAYS = 14;
/** An organizer sends every participant in one request; matches the seat maximum. */
export const REVIEWS_PER_REQUEST_MAX = 200;

const personSchema = z.object({
	id: z.uuid(),
	/** Null: the account was deleted (T-804). */
	displayName: z.string().nullable(),
	photoUrl: z.url().nullable(),
});

const ratingSchema = z.number().int().min(1).max(5);

/** Tags on the event rating (T-905, D45): a participant's review of the organizer only. */
export const REVIEW_TAGS = ['as_described', 'on_time', 'great_vibe', 'well_organized'] as const;
export type ReviewTag = (typeof REVIEW_TAGS)[number];
const tagsSchema = z
	.array(z.enum(REVIEW_TAGS))
	.max(REVIEW_TAGS.length)
	.refine((tags) => new Set(tags).size === tags.length, { message: 'each tag once' });

/** «★ 4.8 · 12»; null below MIN_REVIEWS_FOR_RATING (D13) and for venue events. */
export const ratingSummarySchema = z
	.object({ average: z.number(), count: z.number().int() })
	.nullable();
export type RatingSummary = z.infer<typeof ratingSummarySchema>;

const commentSchema = z
	.string()
	.trim()
	.max(REVIEW_COMMENT_MAX)
	.nullish()
	.transform((value) => (value === undefined || value === null || value === '' ? null : value));

/** `POST /v1/events/:id/reviews`. */
export const submitReviewsRequestSchema = z.strictObject({
	reviews: z
		.array(
			z.strictObject({
				targetId: z.uuid(),
				rating: ratingSchema,
				comment: commentSchema,
				tags: tagsSchema.default([]),
			}),
		)
		.min(1)
		.max(REVIEWS_PER_REQUEST_MAX)
		.refine((items) => new Set(items.map((item) => item.targetId)).size === items.length, {
			message: 'each person once',
		}),
});

/** `GET /v1/events/:id/reviews/mine` and the response of a submit: the S17 sheet state. */
export const eventReviewsSchema = z.object({
	eventId: z.uuid(),
	role: z.enum(['organizer', 'participant']),
	/** End + 14 days. After it, `open` is false and the sheet is read-only. */
	deadline: z.iso.datetime(),
	open: z.boolean(),
	targets: z.array(
		z.object({
			user: personSchema,
			/** Organizer view only: the no-show mark (no-shows can still be rated). */
			noShow: z.boolean(),
			/** False when this person cannot be reviewed (deleted account, or they blocked you). */
			reviewable: z.boolean(),
			mine: z
				.object({
					rating: ratingSchema,
					comment: z.string().nullable(),
					tags: z.array(z.enum(REVIEW_TAGS)),
				})
				.nullable(),
		}),
	),
});

/** `GET /v1/users/:id/reviews`: newest first; empty until `MIN_REVIEWS_FOR_RATING` (D13). */
export const userReviewsQuerySchema = z.object({
	cursor: z.string().max(200).optional(),
	limit: z.coerce.number().int().min(1).max(50).default(20),
});

export const userReviewsResponseSchema = z.object({
	items: z.array(
		z.object({
			id: z.uuid(),
			rating: ratingSchema,
			comment: z.string().nullable(),
			author: personSchema,
			event: z.object({ id: z.uuid(), title: z.string() }),
			createdAt: z.iso.datetime(),
		}),
	),
	nextCursor: z.string().nullable(),
});

export type SubmitReviewsRequest = z.infer<typeof submitReviewsRequestSchema>;
export type EventReviews = z.infer<typeof eventReviewsSchema>;
export type UserReviewsQuery = z.infer<typeof userReviewsQuerySchema>;
export type UserReviewsResponse = z.infer<typeof userReviewsResponseSchema>;
